/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useQueryClient } from '@tanstack/react-query'
import OpalGrid from '../../../../organisms/DenaliAnalysis/tables/operational'
import SydButton from '../../../../commonDesign/Button'
import { useRetryMigrationTask } from '../../../../../api/documentVault'
import { QUERY_KEYS } from '../../../../../api/queryKeys'

const useStyles = makeStyles(() => ({
  wrapper: {
    '& .__errorCode': {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: 'var(--border-radius-sm)',
      backgroundColor: 'var(--color-danger-050)',
      color: 'var(--color-danger-500)',
      fontSize: '0.75rem',
      fontWeight: 600,
      fontFamily: 'monospace'
    },
    '& .__fileName': {
      maxWidth: '280px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& .__docKey': {
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      color: 'var(--color-neutral-500)'
    }
  }
}))

const MigrationTasksTable = ({ tasks, total, disabled, loading, filter, onSortChange, onPageChange, sortState: externalSortState, pageState: externalPageState }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const retryTask = useRetryMigrationTask()

  const handleRetry = useCallback(async (batchTaskId) => {
    await retryTask.mutateAsync(batchTaskId)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.migrationBatches] })
  }, [retryTask, queryClient])

  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'batchTaskId',
        header: 'Task ID',
        size: 80
      },
      {
        id: 'fileName',
        header: 'File Name',
        accessorFn: (row) => row.taskData?.fileName ?? '',
        cell: ({ getValue }) => <div className='__fileName' title={getValue()}>{getValue()}</div>,
        size: 280
      },
      {
        id: 'household',
        header: 'Household',
        accessorFn: (row) => row.taskData?.ocConsumerId ?? '',
        size: 100
      }
    ]

    if (filter === 'completed' || filter === 'all') {
      cols.push({
        id: 'documentKey',
        header: 'Document Key',
        accessorFn: (row) => row.resultData?.documentKey ?? '',
        cell: ({ getValue }) => getValue() ? <span className='__docKey'>{getValue()}</span> : '-',
        enableSorting: false,
        size: 250
      })
    }

    if (filter === 'errored' || filter === 'all') {
      cols.push({
        id: 'errorCode',
        header: 'Error',
        accessorFn: (row) => row.errorData?.errorCode ?? '',
        cell: ({ getValue }) => getValue() ? <span className='__errorCode'>{getValue()}</span> : null,
        enableSorting: false,
        size: 160
      })
      cols.push({
        id: 'errorMessage',
        header: 'Message',
        accessorFn: (row) => row.errorData?.message ?? '',
        cell: ({ getValue }) => (
          <div style={{ maxWidth: '250px', fontSize: '0.8rem', color: 'var(--color-neutral-500)' }}>
            {getValue()}
          </div>
        ),
        enableSorting: false,
        size: 250
      })
    }

    if ((filter === 'errored' || filter === 'all') && !disabled) {
      cols.push({
        id: 'action',
        header: '',
        accessorFn: () => '',
        cell: ({ row }) => {
          if (!row.original.isError) return null
          return (
            <SydButton
              size='sm'
              variant='outline'
              icon='sync'
              onClick={() => handleRetry(row.original.batchTaskId)}
              disabled={disabled || retryTask.isLoading}
              processing={retryTask.isLoading}
            >
              Retry
            </SydButton>
          )
        },
        enableSorting: false,
        size: 100
      })
    }

    return cols
  }, [filter, disabled, handleRetry, retryTask.isLoading])

  const filterLabel = filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)

  return (
    <div className={classes.wrapper}>
      <div className='font-weight-bold' style={{ marginBottom: '8px' }}>
        {filterLabel} Tasks ({total})
      </div>
      <OpalGrid
        data={tasks || []}
        rowCount={total || 0}
        columns={columns}
        isFetching={loading}
        pageState={externalPageState}
        sortState={externalSortState}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
      />
    </div>
  )
}

MigrationTasksTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  total: PropTypes.number,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  filter: PropTypes.string,
  onSortChange: PropTypes.func,
  onPageChange: PropTypes.func,
  sortState: PropTypes.array,
  pageState: PropTypes.object
}

export default MigrationTasksTable
